/**
 * SYNESTHESIA's scene list and pictures on this device (OPBridge S344/S352, `GET /synesthesia/scenes` and
 * `GET /synesthesia/picture`). One Synesthesia per rig, so ONE list: asked of one connected computer whose OPBridge has
 * `synesthesia_control`, which answers from the PC that drives Synesthesia over the peer link (the owner, preferred
 * once known, saves that hop).
 *
 * Read while anything showing it is mounted (`watchSynList`, ref-counted, every `POLL_MS`), on demand
 * (`ensureSynList`, the pickers), and again after a press says the list moved (a scene with no mapping, control
 * switched off). Pictures are fetched once per scene name, a few at a time, kept as blob URLs for the page's life
 * (a rig has tens of scenes), and revoked when a scene leaves the list.
 *
 * Why not per bridge like the Hue list: every PC answers the SAME list (OPBridge forwards to the owner), so asking each
 * would only multiply the reads.
 *
 * Plain signals over immutable records (a Solid read of a Map tracks nothing).
 */

import { createSignal } from 'solid-js';
import { bridges, getConnection, primaryBridge, type BridgeConfig } from '../opbridge/store.ts';
import { OpbridgeError, type SynesthesiaScene, type SynesthesiaScenesResponse } from '../opbridge/httpClient.ts';
import { DEFAULT_META } from './model.ts';

const POLL_MS = 30_000;
const RETRY_MS = 10_000;
const PIC_CONCURRENCY = 3;

export type SynStatus = 'unknown' | 'loading' | 'ok' | 'not_set_up' | 'too_old' | 'no_access' | 'error';
export interface SynListState { status: SynStatus; at: number; rowId: string; detail?: string; data?: SynesthesiaScenesResponse }

const [state, setState] = createSignal<SynListState>({ status: 'unknown', at: 0, rowId: '' });
const [pictures, setPictures] = createSignal<Record<string, string>>({});
/** The PC Synesthesia was last listed on (node id), so the next read asks it first. */
let ownerNode = '';

export const synListState = state;

/** Whether `cfg`'s OPBridge says it has Synesthesia control (`/health.capabilities.synesthesia_control`, S344). It says
 *  the BUILD has it, not that a PC has it switched on. */
export function bridgeHasSynesthesia(cfg: BridgeConfig | null | undefined): boolean {
  return !!cfg && getConnection(cfg.id)?.health()?.capabilities?.['synesthesia_control'] === true;
}
const connected = (cfg: BridgeConfig) => getConnection(cfg.id)?.status() === 'connected';

/** The computer to ask: the owner when it is connected and able, else the primary, else any able one. Null when no
 *  connected computer has the capability (see {@link synSummary} for why). Reactive. */
export function synAskRow(): BridgeConfig | null {
  const up = bridges().filter(connected);
  const able = up.filter(bridgeHasSynesthesia);
  if (able.length === 0) return null;
  const owner = ownerNode ? able.find((b) => b.nodeId === ownerNode) : undefined;
  if (owner) return owner;
  const primary = primaryBridge();
  return able.find((b) => b.id === primary?.id) ?? able[0]!;
}

/** The connected computer that drives Synesthesia, when the list named one we are paired with. */
export function synOwnerRow(): BridgeConfig | null {
  const node = state().data?.nodeId;
  return node ? bridges().find((b) => b.nodeId === node) ?? null : null;
}

const inFlight = { list: false };

/** Read the list now. Never throws. */
export async function refreshSynList(): Promise<void> {
  if (inFlight.list) return;
  const row = synAskRow();
  if (!row) {
    // Say why there is nothing to ask: no computer at all, or none whose OPBridge has Synesthesia. A computer whose
    // health has not been read yet (just connected) decides nothing: stay unknown until it has.
    const up = bridges().filter(connected);
    const known = up.length > 0 && up.every((b) => !!getConnection(b.id)?.health());
    setState({ status: known ? 'too_old' : 'unknown', at: Date.now(), rowId: up[0]?.id ?? '' });
    return;
  }
  inFlight.list = true;
  if (state().status === 'unknown') setState({ status: 'loading', at: Date.now(), rowId: row.id });
  try {
    const res = await getConnection(row.id)!.httpClient().synesthesiaScenes();
    const data: SynesthesiaScenesResponse = {
      ...res,
      scenes: Array.isArray(res?.scenes) ? res.scenes.filter((s) => s && typeof s.name === 'string' && s.name.trim() !== '') : [],
      playlists: Array.isArray(res?.playlists) ? res.playlists : [],
    };
    if (data.nodeId) ownerNode = data.nodeId;
    setState({ status: 'ok', at: Date.now(), rowId: row.id, data });
    void fetchPictures(row.id, data.scenes);
  } catch (err) {
    const code = err instanceof OpbridgeError ? err.httpStatus : undefined;
    const body = err instanceof Error ? err.message : String(err);
    if (code === 404 && body.includes('not_set_up')) setState({ status: 'not_set_up', at: Date.now(), rowId: row.id });
    else if (code === 404) setState({ status: 'too_old', at: Date.now(), rowId: row.id });
    else if (code === 401 || code === 403) setState({ status: 'no_access', at: Date.now(), rowId: row.id });
    else setState({ status: 'error', at: Date.now(), rowId: row.id, detail: body });
  } finally {
    inFlight.list = false;
  }
}

/** Read if never read, or if the last read failed over `RETRY_MS` ago. Cheap from an effect. */
export function ensureSynList(): void {
  const st = state();
  if (st.status === 'ok' || st.status === 'loading') return;
  if (st.status !== 'unknown' && Date.now() - st.at < RETRY_MS) return;
  void refreshSynList();
}

let watchers = 0;
let timer: ReturnType<typeof setInterval> | null = null;
/** Keep the list fresh while the caller is mounted. Returns the release. */
export function watchSynList(): () => void {
  watchers += 1;
  ensureSynList();
  if (!timer) timer = setInterval(() => void refreshSynList(), POLL_MS);
  let released = false;
  return () => {
    if (released) return;
    released = true;
    watchers -= 1;
    if (watchers <= 0 && timer) { clearInterval(timer); timer = null; watchers = 0; }
  };
}

// ---- pictures ----

const picWanted = new Set<string>();
const noPic = new Set<string>();

async function fetchPictures(rowId: string, scenes: SynesthesiaScene[]): Promise<void> {
  const names = new Set(scenes.map((s) => s.name));
  // A scene that left the list gives its picture back.
  const have = pictures();
  const gone = Object.keys(have).filter((n) => !names.has(n));
  if (gone.length) {
    for (const n of gone) URL.revokeObjectURL(have[n]!);
    setPictures((all) => { const next = { ...all }; for (const n of gone) delete next[n]; return next; });
  }
  const todo = scenes.filter((s) => s.picture && !pictures()[s.name] && !picWanted.has(s.name) && !noPic.has(s.name)).map((s) => s.name);
  for (const n of todo) picWanted.add(n);
  let next = 0;
  const worker = async (): Promise<void> => {
    while (next < todo.length) {
      const name = todo[next++]!;
      try {
        const conn = getConnection(rowId);
        if (!conn) return;
        const blob = await conn.httpClient().synesthesiaPicture(name);
        const url = URL.createObjectURL(blob);
        setPictures((all) => ({ ...all, [name]: url }));
      } catch (err) {
        // 404: this scene has no picture; anything else is retried on the next read.
        if (err instanceof OpbridgeError && err.httpStatus === 404) noPic.add(name);
      } finally {
        picWanted.delete(name);
      }
    }
  };
  await Promise.all(Array.from({ length: Math.min(PIC_CONCURRENCY, todo.length) }, worker));
}

/** A scene's picture as a blob URL, or null (none, or not fetched yet). Reactive. */
export function synPicture(name: string | undefined): string | null {
  return name ? pictures()[name] ?? null : null;
}

// ---- reading it ----

export const synList = (): SynesthesiaScenesResponse | null => (state().status === 'ok' ? state().data ?? null : null);
export const synScenes = (): SynesthesiaScene[] => synList()?.scenes ?? [];
export const synSceneNamed = (name: string | undefined): SynesthesiaScene | null => (name ? synScenes().find((s) => s.name === name) ?? null : null);
/** The meta controls that OPBridge takes (its list), else the S352 seven. */
export const synMeta = (): Array<{ name: string; type: string }> => {
  const m = synList()?.metaControls;
  return Array.isArray(m) && m.length > 0 ? m.filter((x) => x && typeof x.name === 'string') : DEFAULT_META;
};
export const synCommands = (): string[] | null => synList()?.commands ?? null;
/** The PC that drives Synesthesia, by name ("STREAMPC"), when listed. */
export const synWhere = (): string => synList()?.pc ?? '';

export type SynSummaryState = 'ok' | 'reading' | 'nobridge' | 'not_set_up' | 'too_old' | 'no_access' | 'error';
/** Whether Synesthesia can be used from here, and why not in words a performer can act on. Reactive. */
export function synSummary(): { state: SynSummaryState; text: string; short: string } {
  const up = bridges().filter(connected);
  if (up.length === 0) return { state: 'nobridge', text: 'No computer is connected.', short: 'offline' };
  if (!up.some(bridgeHasSynesthesia)) {
    if (!up.every((b) => !!getConnection(b.id)?.health())) return { state: 'reading', text: "Reading Synesthesia's scenes...", short: 'reading' };
    return {
      state: 'too_old',
      text: "This computer's OPBridge is too old for Synesthesia: update it (v1.15.231 or later).",
      short: 'update OPBridge',
    };
  }
  const st = state();
  switch (st.status) {
    case 'ok': return { state: 'ok', text: '', short: '' };
    case 'unknown': case 'loading': return { state: 'reading', text: "Reading Synesthesia's scenes...", short: 'reading' };
    case 'not_set_up': return {
      state: 'not_set_up',
      text: "Synesthesia is not set up on any PC. On the PC that runs it, open OPBridge's dashboard, Synesthesia, and switch control on.",
      short: 'not set up',
    };
    case 'too_old': return { state: 'too_old', text: "That computer's OPBridge is too old for Synesthesia: update it (v1.15.231 or later).", short: 'update OPBridge' };
    case 'no_access': return { state: 'no_access', text: 'OPBridge has not given this device Synesthesia. Pair it again from Computers.', short: 'pair again' };
    default: return { state: 'error', text: `Could not read Synesthesia's scenes${st.detail ? ` (${st.detail})` : ''}.`, short: 'not reachable' };
  }
}

/** Mark the list as not set up at once (a press was told so), then read it again. */
export function noteNotSetUp(): void {
  const st = state();
  setState({ ...st, status: 'not_set_up', at: Date.now() });
  setTimeout(() => void refreshSynList(), 1500);
}

/** Tests and the bot: forget everything (a fresh page). */
export function resetSynList(): void {
  for (const url of Object.values(pictures())) URL.revokeObjectURL(url);
  setPictures({});
  picWanted.clear(); noPic.clear(); ownerNode = '';
  setState({ status: 'unknown', at: 0, rowId: '' });
}
